<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import TutorInfo from '@/Components/Library/TutorInfo.vue'
import {
  ArrowLeft,
  Play,
  ThumbsUp,
  ThumbsDown,
  Share2,
  Bookmark,
  Clock,
  Eye,
  MessageSquare,
  MoreHorizontal,
  Send,
  Smile,
} from 'lucide-vue-next'

interface Subject {
  id: number
  name: string
}

interface Level {
  id: number
  name: string
}

interface Examboard {
  id: number
  name: string
}

interface Content {
  id: number
  title: string
  slug: string
  description: string
  type: string
  duration: number
  formatted_duration: string
  thumbnail_url: string
  stream_url: string
  mux_playback_id: string
  view_count: number
  comment_count: number
  is_free: boolean
  published_at: string
  published_at_human: string
  subject: Subject | null
  level: Level | null
  examboard: Examboard | null
  user_progress: number
  is_completed: boolean
}

interface Teacher {
  id: number
  name: string
  slug: string
  avatar_url: string | null
  bio: string | null
  tagline: string | null
  content_count: number
  student_count: number
}

interface RelatedVideo {
  id: number
  title: string
  slug: string
  thumbnail_url: string
  duration: number
  formatted_duration: string
  view_count: number
  published_at_human: string
  teacher_name: string
}

interface CommentUser {
  id: number
  name: string
  avatar_url: string | null
  is_teacher: boolean
}

interface Reply {
  id: number
  body: string
  user: CommentUser
  created_at: string
}

interface Comment {
  id: number
  body: string
  user: CommentUser
  created_at: string
  replies: Reply[]
}

interface UserProgress {
  progress: number
  is_completed: boolean
}

const props = defineProps<{
  content: Content
  teacher: Teacher
  relatedVideos: RelatedVideo[]
  comments: Comment[]
  userProgress: UserProgress
}>()

const videoRef = ref<HTMLVideoElement | null>(null)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(props.content.duration || 0)
const watchedSinceReport = ref(0)
const showControls = ref(true)
const newComment = ref('')
const isPostingComment = ref(false)

const progressPercentage = computed(() => {
  if (!duration.value) return 0
  return (currentTime.value / duration.value) * 100
})

const formattedCurrentTime = computed(() => {
  return formatTime(currentTime.value)
})

const formattedDuration = computed(() => {
  return formatTime(duration.value)
})

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatViewCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`
  }
  return count.toString()
}

function playVideo() {
  if (videoRef.value) {
    videoRef.value.play()
    isPlaying.value = true
  }
}

function pauseVideo() {
  if (videoRef.value) {
    videoRef.value.pause()
    isPlaying.value = false
  }
}

function togglePlay() {
  if (isPlaying.value) {
    pauseVideo()
  } else {
    playVideo()
  }
}

/*
 * Time watched is accumulated from the gap between `timeupdate` ticks rather
 * than read off the playhead, so seeking forward earns the tutor nothing and
 * rewatching earns them again. A jump wider than a tick is a seek, not viewing.
 */
const MAXIMUM_TICK_SECONDS = 2
const REPORT_EVERY_SECONDS = 10

function handleTimeUpdate() {
  if (!videoRef.value) {
    return
  }

  const previous = currentTime.value
  currentTime.value = videoRef.value.currentTime

  const elapsed = currentTime.value - previous

  if (elapsed > 0 && elapsed <= MAXIMUM_TICK_SECONDS) {
    watchedSinceReport.value += elapsed
  }

  if (watchedSinceReport.value >= REPORT_EVERY_SECONDS) {
    updateProgress()
  }
}

function handleLoadedMetadata() {
  if (videoRef.value) {
    duration.value = videoRef.value.duration
  }
}

function seekTo(event: MouseEvent) {
  const progressBar = event.currentTarget as HTMLElement
  const rect = progressBar.getBoundingClientRect()
  const percent = (event.clientX - rect.left) / rect.width
  if (videoRef.value) {
    videoRef.value.currentTime = percent * duration.value
  }
}

async function updateProgress() {
  const progress = Math.floor((currentTime.value / duration.value) * 100)
  const completed = progress >= 95
  const secondsWatched = Math.round(watchedSinceReport.value)

  // Cleared before the request, not after: a failed report loses ten seconds
  // rather than counting them again on the next one.
  watchedSinceReport.value = 0

  try {
    await window.axios.post(`/student/explore/${props.content.id}/progress`, {
      progress,
      completed,
      seconds_watched: secondsWatched,
      position_seconds: Math.round(currentTime.value),
    })
  } catch (error) {
    console.error('Failed to update progress:', error)
  }
}

async function postComment() {
  if (!newComment.value.trim() || isPostingComment.value) return

  isPostingComment.value = true
  try {
    // TODO: Implement comment posting API
    // await window.axios.post(`/student/explore/${props.content.id}/comments`, {
    //   body: newComment.value
    // })
    newComment.value = ''
    // Refresh comments
  } catch (error) {
    console.error('Failed to post comment:', error)
  } finally {
    isPostingComment.value = false
  }
}

function goToContent(contentId: number) {
  router.visit(`/student/explore/${contentId}`)
}

onMounted(() => {
  // Set initial progress if resuming
  if (videoRef.value && props.userProgress.progress > 0) {
    const startTime = (props.userProgress.progress / 100) * props.content.duration
    videoRef.value.currentTime = startTime
    currentTime.value = startTime
  }
})

onUnmounted(() => {
  // Save progress when leaving
  if (currentTime.value > 0) {
    updateProgress()
  }
})
</script>

<template>
  <Head :title="content.title" />

  <DashboardLayout>
    <div class="w-full max-w-7xl">
      <!-- Back Button -->
      <Link
        href="/student/explore"
        class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
      >
        <ArrowLeft class="w-4 h-4" />
        Back to Explore
      </Link>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Main Content (2 cols) -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Video Player -->
          <section class="bg-black rounded-xl overflow-hidden">
            <div class="relative aspect-video">
              <!-- Video Element -->
              <video
                ref="videoRef"
                :src="content.stream_url"
                :poster="content.thumbnail_url"
                class="w-full h-full object-contain"
                @timeupdate="handleTimeUpdate"
                @loadedmetadata="handleLoadedMetadata"
                @play="isPlaying = true"
                @pause="isPlaying = false"
                @click="togglePlay"
              />

              <!-- Play Button Overlay (when paused) -->
              <div
                v-if="!isPlaying"
                class="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                @click="playVideo"
              >
                <button class="w-20 h-20 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all shadow-2xl">
                  <Play class="w-8 h-8 text-blue-600 ml-1" />
                </button>
              </div>

              <!-- Controls -->
              <div class="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 to-transparent p-4">
                <div class="flex items-center justify-between mb-2 text-white text-sm">
                  <span>{{ formattedCurrentTime }} / {{ formattedDuration }}</span>
                </div>
                <!-- Progress Bar -->
                <div
                  class="h-1.5 bg-gray-600 rounded-full cursor-pointer"
                  @click="seekTo"
                >
                  <div
                    class="h-full bg-blue-500 rounded-full transition-all"
                    :style="{ width: `${progressPercentage}%` }"
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- Video Info -->
          <section class="bg-white rounded-xl border border-gray-200 p-6">
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-3">
                  <span v-if="content.subject" class="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded">
                    {{ content.subject.name }}
                  </span>
                  <span v-if="content.level" class="bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded">
                    {{ content.level.name }}
                  </span>
                  <span v-if="content.is_free" class="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded">
                    FREE
                  </span>
                  <span class="text-gray-500 text-sm">• {{ content.published_at_human }}</span>
                </div>
                <h2 class="text-2xl font-bold text-gray-900 mb-2">{{ content.title }}</h2>
                <p class="text-gray-600 leading-relaxed">{{ content.description }}</p>
              </div>
            </div>

            <!-- Like/Dislike/Share/Save controls - hidden for now, will bring back later
            <div class="flex items-center justify-between pt-4 border-t border-gray-200">
              <div class="flex items-center gap-6">
                <button class="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors group">
                  <div class="w-10 h-10 bg-gray-100 group-hover:bg-blue-50 rounded-full flex items-center justify-center transition-colors">
                    <ThumbsUp class="w-5 h-5" />
                  </div>
                  <span class="text-sm font-medium">Like</span>
                </button>
                <button class="flex items-center gap-2 text-gray-700 hover:text-red-500 transition-colors group">
                  <div class="w-10 h-10 bg-gray-100 group-hover:bg-red-50 rounded-full flex items-center justify-center transition-colors">
                    <ThumbsDown class="w-5 h-5" />
                  </div>
                  <span class="text-sm font-medium">Dislike</span>
                </button>
                <button class="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors group">
                  <div class="w-10 h-10 bg-gray-100 group-hover:bg-purple-50 rounded-full flex items-center justify-center transition-colors">
                    <Share2 class="w-5 h-5" />
                  </div>
                  <span class="text-sm font-medium">Share</span>
                </button>
                <button class="flex items-center gap-2 text-gray-700 hover:text-green-600 transition-colors group">
                  <div class="w-10 h-10 bg-gray-100 group-hover:bg-green-50 rounded-full flex items-center justify-center transition-colors">
                    <Bookmark class="w-5 h-5" />
                  </div>
                  <span class="text-sm font-medium">Save</span>
                </button>
              </div>
              <div class="flex items-center gap-2 text-sm text-gray-500">
                <Eye class="w-4 h-4" />
                <span>{{ formatViewCount(content.view_count) }} views</span>
              </div>
            </div>
            -->
          </section>

          <!-- Tutor Info -->
          <TutorInfo
            :tutor="{
              id: teacher.id,
              slug: teacher.slug,
              name: teacher.name,
              avatar: teacher.avatar_url,
              specialization: teacher.tagline || 'Expert Tutor',
              videoCount: teacher.content_count || 0,
              studentCount: 0,
            }"
            tutor-link-prefix="/student/tutors"
          />

          <!-- Comments Section -->
          <section class="bg-white rounded-xl border border-gray-200 p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare class="w-5 h-5" />
                Comments
                <span class="text-gray-500 font-normal">({{ comments.length }})</span>
              </h3>
            </div>

            <!-- Add Comment -->
            <div class="mb-6">
              <div class="flex items-start gap-3">
                <div class="w-10 h-10 rounded-full bg-linear-to-br from-teal-500 to-teal-800 flex items-center justify-center text-white font-semibold shrink-0">
                  U
                </div>
                <div class="flex-1">
                  <textarea
                    v-model="newComment"
                    placeholder="Add a comment..."
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="3"
                  />
                  <div class="flex items-center justify-between mt-3">
                    <div class="flex items-center gap-2">
                      <button class="text-gray-500 hover:text-gray-700 p-2">
                        <Smile class="w-5 h-5" />
                      </button>
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        class="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
                        @click="newComment = ''"
                      >
                        Cancel
                      </button>
                      <button
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        :disabled="!newComment.trim() || isPostingComment"
                        @click="postComment"
                      >
                        <Send class="w-4 h-4" />
                        Post
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comments List -->
            <div v-if="comments.length === 0" class="text-center py-8 text-gray-400">
              <MessageSquare class="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p class="font-medium text-gray-600">No comments yet</p>
              <p class="text-sm">Be the first to comment!</p>
            </div>

            <div v-else class="space-y-6">
              <div v-for="comment in comments" :key="comment.id" class="flex items-start gap-3">
                <img
                  v-if="comment.user.avatar_url"
                  :src="comment.user.avatar_url"
                  :alt="comment.user.name"
                  class="w-10 h-10 rounded-full border-2 border-gray-200 object-cover shrink-0"
                />
                <div v-else class="w-10 h-10 rounded-full bg-linear-to-br from-teal-500 to-teal-800 flex items-center justify-center text-white font-semibold shrink-0">
                  {{ comment.user.name.charAt(0).toUpperCase() }}
                </div>
                <div class="flex-1">
                  <div class="bg-gray-50 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center gap-2">
                        <span class="font-semibold text-gray-900 text-sm">{{ comment.user.name }}</span>
                        <span
                          v-if="comment.user.is_teacher"
                          class="bg-blue-600 text-white text-xs px-2 py-0.5 rounded"
                        >
                          Tutor
                        </span>
                        <span class="text-gray-500 text-xs">{{ comment.created_at }}</span>
                      </div>
                      <button class="text-gray-400 hover:text-gray-600">
                        <MoreHorizontal class="w-4 h-4" />
                      </button>
                    </div>
                    <p class="text-gray-700 text-sm leading-relaxed">{{ comment.body }}</p>
                  </div>

                  <!-- Replies -->
                  <div v-if="comment.replies.length > 0" class="mt-4 ml-8 space-y-4">
                    <div v-for="reply in comment.replies" :key="reply.id" class="flex items-start gap-3">
                      <img
                        v-if="reply.user.avatar_url"
                        :src="reply.user.avatar_url"
                        :alt="reply.user.name"
                        class="w-8 h-8 rounded-full border-2 border-gray-200 object-cover shrink-0"
                      />
                      <div v-else class="w-8 h-8 rounded-full bg-linear-to-br from-teal-500 to-teal-800 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                        {{ reply.user.name.charAt(0).toUpperCase() }}
                      </div>
                      <div class="flex-1">
                        <div :class="[reply.user.is_teacher ? 'bg-blue-50' : 'bg-gray-50', 'rounded-lg p-3']">
                          <div class="flex items-center gap-2 mb-1">
                            <span class="font-semibold text-gray-900 text-sm">{{ reply.user.name }}</span>
                            <span
                              v-if="reply.user.is_teacher"
                              class="bg-blue-600 text-white text-xs px-2 py-0.5 rounded"
                            >
                              Tutor
                            </span>
                            <span class="text-gray-500 text-xs">{{ reply.created_at }}</span>
                          </div>
                          <p class="text-gray-700 text-sm leading-relaxed">{{ reply.body }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Sidebar (1 col) - Related Videos -->
        <div class="lg:col-span-1">
          <section class="bg-white rounded-xl border border-gray-200 p-5">
            <h3 class="text-lg font-bold text-gray-900 mb-4">Related Videos</h3>

            <div v-if="relatedVideos.length === 0" class="text-center py-8 text-gray-400">
              <Play class="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p class="text-sm">No related videos</p>
            </div>

            <div v-else class="space-y-4">
              <div
                v-for="video in relatedVideos"
                :key="video.id"
                class="flex gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors -mx-2"
                @click="goToContent(video.id)"
              >
                <!-- Thumbnail -->
                <div class="relative w-36 h-20 bg-gray-200 rounded-lg shrink-0 overflow-hidden">
                  <img
                    v-if="video.thumbnail_url"
                    :src="video.thumbnail_url"
                    :alt="video.title"
                    class="w-full h-full object-cover"
                  />
                  <div v-else class="w-full h-full bg-linear-to-br from-teal-500 to-teal-800 flex items-center justify-center">
                    <Play class="w-6 h-6 text-white opacity-80" />
                  </div>
                  <div class="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    {{ video.formatted_duration }}
                  </div>
                </div>

                <!-- Info -->
                <div class="flex-1 min-w-0">
                  <h4 class="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">{{ video.title }}</h4>
                  <p class="text-xs text-gray-600 mb-1">{{ video.teacher_name }}</p>
                  <p class="text-xs text-gray-500">
                    {{ formatViewCount(video.view_count) }} views • {{ video.published_at_human }}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>
